import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { StorageService } from '../storage/storage.service';
import { Account, AccountPlan, AuthSession, AuthUser, TwoFaMethod } from './auth.models';
import { AuthApiService } from './auth-api.service';

const SESSION_KEY = 'vendor_portal_session_v1';

function emptySession(): AuthSession {
  return { user: null, isLoggedIn: false, isTwoFaVerified: false };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly sessionSubject = new BehaviorSubject<AuthSession>(this.loadSession());
  readonly session$ = this.sessionSubject.asObservable();

  constructor(
    private readonly storage: StorageService,
    private readonly authApi: AuthApiService
  ) {}

  get snapshot(): AuthSession {
    return this.sessionSubject.value;
  }

  /** Convenience: selected account (if any). */
  get selectedAccount(): Account | null {
    const s = this.snapshot;
    const tin = s.selectedTin;
    if (!tin || !s.user) return null;
    return s.user.accounts.find((a) => a.tin === tin) ?? null;
  }

  /** Convenience: plan derived from selected account. */
  get selectedPlan(): AccountPlan | null {
    return this.selectedAccount?.plan ?? null;
  }

  /** True when user is verified but must pick an account (has >1 and none selected). */
  requiresAccountSelection(): boolean {
    const s = this.snapshot;
    if (!s.isLoggedIn || !s.isTwoFaVerified || !s.user) return false;
    return s.user.accounts.length > 1 && !s.selectedTin;
  }

  /** Select a TIN scope for the session. */
  selectAccount(tin: string): void {
    const s = this.snapshot;
    if (!s.isLoggedIn || !s.isTwoFaVerified || !s.user) return;
    const exists = s.user.accounts.some((a) => a.tin === tin);
    if (!exists) return;
    this.setSession({ ...s, selectedTin: tin });
  }

  async login(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await this.authApi.login(email.trim(), password);
      const user: AuthUser = {
        email: res.email,
        accounts: (res.accounts ?? []).filter((a) => !!a?.tin),
      };

      if (!user.accounts.length) {
        return { ok: false, error: 'No linked businesses were found for this user.' };
      }

      const next: AuthSession = {
        user,
        isLoggedIn: true,
        isTwoFaVerified: false,
      };

      this.setSession(next);
      return { ok: true };
    } catch {
      return { ok: false, error: 'Invalid email or password.' };
    }
  }

  selectTwoFaMethod(method: TwoFaMethod): void {
    const current = this.snapshot;
    if (!current.isLoggedIn) return;

    this.setSession({
      ...current,
      twoFaMethod: method,
      isTwoFaVerified: false,
    });
  }

  verifyTwoFa(code: string): { ok: boolean; error?: string } {
    const current = this.snapshot;
    if (!current.isLoggedIn) return { ok: false, error: 'Not authenticated.' };

    // Prototype rule: magic code only
    if (code !== '000000') return { ok: false, error: 'Invalid verification code.' };

    // If the user only has one account, auto-select it.
    const onlyOneTin = current.user?.accounts?.length === 1 ? current.user.accounts[0].tin : undefined;
    this.setSession({ ...current, isTwoFaVerified: true, selectedTin: onlyOneTin });
    return { ok: true };
  }

  logout(): void {
    this.setSession(emptySession());
  }

  private loadSession(): AuthSession {
    const stored = this.storage.get<AuthSession>(SESSION_KEY);
    if (!stored) return emptySession();
    // Basic shape guard
    if (typeof stored.isLoggedIn !== 'boolean' || typeof stored.isTwoFaVerified !== 'boolean') {
      return emptySession();
    }
    return stored;
  }

  private setSession(next: AuthSession): void {
    this.storage.set<AuthSession>(SESSION_KEY, next);
    this.sessionSubject.next(next);
  }
}
